import { useState, useEffect, useRef } from "react";
import AIAnimation from "./AIAnimation";
import ScrollReveal from "./ScrollReveal";

const TechShowcase = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const technologies = [
    {
      name: "React",
      icon: "⚛️",
      color: "from-cyan-500 to-blue-600"
    },
    {
      name: "Node.js",
      icon: "🟢",
      color: "from-green-500 to-emerald-600"
    },
    {
      name: "Python",
      icon: "🐍",
      color: "from-blue-500 to-indigo-600"
    },
    {
      name: "TensorFlow",
      icon: "🧠",
      color: "from-orange-500 to-red-600"
    },
    {
      name: "AWS",
      icon: "☁️",
      color: "from-yellow-500 to-orange-600"
    },
    {
      name: "Docker",
      icon: "🐳",
      color: "from-blue-600 to-cyan-600"
    },
    {
      name: "MongoDB",
      icon: "🍃",
      color: "from-green-600 to-teal-600"
    },
    {
      name: "GraphQL",
      icon: "📊",
      color: "from-pink-500 to-purple-600"
    }
  ];

  return (
    <div ref={sectionRef} className="relative py-24 overflow-hidden bg-gradient-to-br from-gray-900 via-indigo-900 to-purple-900">
      {/* AI Neural Network Animation */}
      <div className="absolute inset-0">
        <AIAnimation />
      </div>

      {/* Overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-900/50 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-1000 transform ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-indigo-500/20 border border-indigo-400/30 mb-6 backdrop-blur-sm">
            <span className="w-2 h-2 bg-indigo-400 rounded-full mr-2 animate-pulse"></span>
            <span className="text-sm font-medium text-indigo-300 uppercase tracking-wider">
              Powered by Advanced Technology
            </span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Built with Cutting-Edge
          </h2>
          <h3 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent mb-6">
            Technologies & AI
          </h3>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            We leverage the latest technologies and AI frameworks to build scalable, 
            high-performance solutions that drive real business results.
          </p>
        </div>

        {/* Technology Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {technologies.map((tech, index) => (
            <ScrollReveal
              key={tech.name}
              direction={index % 2 === 0 ? "left" : "right"}
              delay={index * 100}
              duration={800}
            >
              <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-white/30 transition-all duration-300 hover:scale-105 hover:bg-white/10">
                {/* Glow effect */}
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${tech.color} opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-300`}></div>
                
                <div className="relative z-10 text-center">
                  <div className="text-4xl mb-3">{tech.icon}</div>
                  <h4 className="text-white font-semibold">{tech.name}</h4>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>

        {/* Stats */}
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-8 transition-all duration-1000 delay-500 transform ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          {[
            { number: "99.9%", label: "Uptime Guarantee" },
            { number: "50ms", label: "API Response Time" },
            { number: "10x", label: "Performance Boost" },
            { number: "24/7", label: "AI Monitoring" }
          ].map((stat, index) => (
            <div key={stat.label} className="text-center group">
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent group-hover:scale-110 transition-transform duration-300">
                {stat.number}
              </div>
              <div className="text-sm text-gray-400 mt-2 group-hover:text-gray-300 transition-colors duration-300">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className={`mt-16 text-center transition-all duration-1000 delay-700 transform ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <button className="group relative overflow-hidden bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white px-8 py-4 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/50">
            <span className="relative z-10 flex items-center justify-center">
              Explore Our Tech Stack
              <svg className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </span>
            
            {/* Shine effect */}
            <div className="absolute inset-0 -left-full group-hover:left-full w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-all duration-700"></div>
          </button>
        </div>
      </div>

      {/* Decorative elements */}
      <div className="absolute top-10 left-10 w-20 h-20 border border-indigo-500/30 rounded-full animate-ping"></div>
      <div className="absolute bottom-10 right-10 w-32 h-32 border border-purple-500/30 rounded-full animate-pulse"></div>
    </div>
  );
};

export default TechShowcase;
